import { useLocalization } from "@fluent/react";
import {
  useBreakpoints,
  Table,
  Button,
  Dialog,
  TruncatedAccount,
} from "@narmi/design_system";
import { cents } from "byzantine";
import Pagination from "byzantine/src/Pagination";
import filters from "byzantine/src/filters";
import PageNavigation from "../../page_navigation/PageNavigation";
import { DIALOG_PAGE_SIZE, getDialogWidth } from "../common";
import styles from "./WirePaymentsTableScreen.module.scss";
import logoStyles from "../../PreviewCard/Logo.module.scss";

const COL_LAYOUT = {
  s: "1.5fr 1.25fr 2.5fr 0.75fr",
  m: "1.5fr 1.25fr 2.5fr 0.75fr",
  l: "1.5fr 1.25fr 2.5fr 0.75fr",
};

const COL_VISIBILITY: React.ComponentProps<
  typeof Table
>["colVisibility"] = ["*", "*", "*", "*"];

const WireReviewPaymentDialog = ({
  isOpen,
  handleClose,
  payments,
  pageNumber,
  setPageNumber,
}: {
  isOpen: boolean;
  handleClose: () => void;
  payments: API.WireImport.CSVParsedRow[];
  pageNumber: number;
  setPageNumber: (page: number) => void;
}) => {
  const { l10n } = useLocalization();
  const { m, l } = useBreakpoints();

  const pagination = new Pagination({
    total: payments.length,
    page: pageNumber,
    pageSize: DIALOG_PAGE_SIZE,
    navigatePage: (page: number) => {
      setPageNumber(page);
    },
  });

  const startIndex = (pagination.page - 1) * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const currentPayments = payments.slice(startIndex, endIndex);

  const renderRecipientCell = (payment: API.WireImport.CSVParsedRow) => {
    const { recipient, recipient_name: recipientName } = payment;
    return (
      <div className={styles.nameContainer}>
        <span className={styles.nameEllipsis}>
          {recipient?.name || recipientName || ""}
        </span>
        <div className={styles.institutionNameContainer}>
          <div className={logoStyles.logo}>
            <span className="narmi-icon-bank" />
          </div>
          <span className="fontColor--secondary fontSize--xs">
            <TruncatedAccount
              lastFour={
                recipient?.fedwire_destination?.account_number?.slice(
                  -4
                ) ?? ""
              }
              name={
                recipient?.fedwire_destination?.institution_name ?? ""
              }
            />
          </span>
        </div>
      </div>
    );
  };

  return (
    <Dialog
      isOpen={isOpen}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button
            onClick={handleClose}
            testId="close-wire-review-payment-dialog"
          >
            {l10n.getString("label-close")}
          </Button>
        </div>
      }
      onUserDismiss={handleClose}
      title={l10n.getString("wire-review-dialog-title")}
      width={getDialogWidth({ m, l })}
    >
      <Table colVisibility={COL_VISIBILITY} colLayout={COL_LAYOUT}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-recipient")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-reason")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-memo")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("wire-payments-table-col-amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {currentPayments.map((payment, index) => {
            const amount = payment.amount
              ? filters.currency(cents(Math.round(payment.amount * 100)))
              : "";

            return (
              <Table.Row key={`wire-review-row__${startIndex + index}`}>
                <Table.Cell>{renderRecipientCell(payment)}</Table.Cell>
                <Table.Cell>{payment.wire_reason || ""}</Table.Cell>
                <Table.Cell>{payment.memo || ""}</Table.Cell>
                <Table.Cell>{amount}</Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
      <PageNavigation
        pagination={pagination}
        marginClasses="margin--all--l"
      />
    </Dialog>
  );
};

export default WireReviewPaymentDialog;
